<template>
    <div ref="containerRef" class="w-full h-full relative" @click="allowViewImage ? viewImage(imageSrc) : null">
        
        <div class="device-wrapper p-2 bg-white/50 rounded-xl md:rounded-3xl absolute top-4 left-1/2 -translate-x-1/2"
            :style="viewStyles">
            <div class="bg-radial from-slate-400 to-slate-600 rounded-xl md:rounded-3xl relative overflow-hidden"
                :style="imageStyles">
                <Transition name="fade" mode="out-in">

                    <img v-if="!$slots.default" :key="imageSrc" :src="imageSrc" alt="Device"
                        class="rounded-xl md:rounded-3xl w-full device-image" :style="imageStyles">
                    <slot v-else />
                </Transition>
                <div v-if="showTime"
                    class="absolute top-0 left-0 w-full h-5 bg-black/50 flex justify-between items-center px-4 text-white text-xs">
                    <div>1:23pm</div>
                    <div>88%</div>
                </div>
            </div>
        </div>
        <slot name="overlay" />
    </div>
</template>


<script setup>
import { computed, ref, onMounted, onUnmounted, inject } from 'vue';


const viewImage = inject('viewImage')

const props = defineProps({
    imageSrc: {
        type: String,        
    },
    x: {
        type: Number,
        default: 0
    },
    y: {
        type: Number,
        default: 0
    },
    z: {
        type: Number,
        default: 0
    },
    width: {
        type: Number,
        default: 80
    },
    deviceType: {
        type: String,
        default: 'phone',
        validator: (value) => ['phone', 'tablet', 'laptop'].includes(value)
    },
    rotateOnScroll:  {
        type: Boolean,
        default: false
    },
    allowViewImage: {
        type: Boolean,
        default: true
    }
})

const containerRef = ref(null)
const scrollPosition = ref(0)

function updateScrollPosition() {
    if (!containerRef.value) return
    const rect = containerRef.value.getBoundingClientRect()
    scrollPosition.value = Math.max(0, -rect.top)
}

onMounted(() => {
    if (props.rotateOnScroll) {
        window.addEventListener('scroll', updateScrollPosition, { passive: true })
        updateScrollPosition()
    }
})

onUnmounted(() => {
    if (props.rotateOnScroll) {
        window.removeEventListener('scroll', updateScrollPosition)
    }
})

const rotation = computed(() => {
    return {
        x: props.x,// + (scrollPosition.value * 0.1),
        y: Math.min(Math.max(props.y + (scrollPosition.value * 0.1), -40), 40),
        z: props.z// + (scrollPosition.value * 0.1)
    }
})

const showTime = computed(() => {
    return false && (props.deviceType === 'phone' || props.deviceType === 'tablet')
})

const viewStyles = computed(() => {
    const { x, y, z } = props.rotateOnScroll ? rotation.value : { x: props.x, y: props.y, z: props.z }
    return generatePhoneViewStyles(x, y, z)
})

const imageStyles = computed(() => {

    switch (props.deviceType) {
        case 'phone':
            return {
                aspectRatio: '1242/2688'
            }
        case 'tablet':
            return {
                aspectRatio: '1024/1366'
            }
        case 'laptop':
            return {
                aspectRatio: '5/3'
            }
        case 'desktop':
            return {
                aspectRatio: '4/3'
            }
    }
})

/**
 * Generates CSS transform and box-shadow styles based on rotation angles
 * Matches the style patterns used in phone-view-1 through phone-view-4
 * @param {number} x - Rotation around X axis in degrees
 * @param {number} y - Rotation around Y axis in degrees
 * @param {number} z - Rotation around Z axis in degrees
 * @returns {object} Object with transform and boxShadow properties
 */
function generatePhoneViewStyles(x = 0, y = 0, z = 0) {
    // Generate transform
    const transform = `perspective(1000px) rotateX(${x}deg) rotateY(${y}deg) rotateZ(${z}deg)`

    // Calculate base shadow offsets
    // Y rotation primarily affects X shadow: negative Y -> positive X, positive Y -> negative X
    const shadowXFromY = Math.round(-y * 0.06) // -50deg -> 3px, 30deg -> -2px

    // X rotation affects both X (negative) and Y (positive) shadows
    const shadowXFromX = Math.round(-x * 0.06) // 50deg -> -3px
    const shadowYFromX = x !== 0 ? Math.round(x * 0.06) : (y !== 0 ? 1 : 3) // X=50 -> 3px, Y rotated -> 1px, else 3px

    // Z rotation (when combined with X) affects both
    const shadowXFromZ = (z !== 0 && x !== 0) ? Math.round(-z * 0.06) : 0
    const shadowYFromZ = (z !== 0 && x !== 0) ? Math.round(-z * 0.06) : 0

    const baseShadowX = shadowXFromY + shadowXFromX + shadowXFromZ
    const baseShadowY = shadowYFromX + shadowYFromZ

    // Generate layered box-shadow
    const shadowLayers = []
    const shadowColor = '#BBB'
    const finalShadowColor = (x === 0 && y === 0 && z === 0) ? '#000000' : '#666'

    // Different patterns based on rotation combinations
    if (x !== 0 && z !== 0) {
        // Pattern for X+Z rotation (like phone-view-1): linear progression
        for (let i = 1; i <= 4; i++) {
            shadowLayers.push(`${baseShadowX * i}px ${baseShadowY * i}px 0 ${shadowColor}`)
        }
        shadowLayers.push(`${Math.round(baseShadowX * 4.67)}px ${Math.round(baseShadowY * 3.33)}px 20px ${finalShadowColor}`)
    } else if (y !== 0) {
        // Pattern for Y rotation (like phone-view-2, phone-view-4): non-linear
        shadowLayers.push(`${baseShadowX}px ${baseShadowY}px 0 ${shadowColor}`)
        shadowLayers.push(`${baseShadowX * 3}px ${baseShadowY * 2}px 0 ${shadowColor}`)
        shadowLayers.push(`${baseShadowX * 4}px ${baseShadowY * 3}px 0 ${shadowColor}`)
        shadowLayers.push(`${Math.round(baseShadowX * 3.33)}px ${baseShadowY * 4}px 0 ${shadowColor}`)
        shadowLayers.push(`0px ${baseShadowY * 7}px 20px ${finalShadowColor}`)
    } else {
        // Pattern for no rotation (like phone-view-3): vertical only
        shadowLayers.push(`0px ${baseShadowY}px 0 ${shadowColor}`)
        shadowLayers.push(`0px ${baseShadowY + 1}px 0 ${shadowColor}`)
        shadowLayers.push(`0px ${baseShadowY + 2}px 0 ${shadowColor}`)
        shadowLayers.push(`0px ${baseShadowY + 4}px 0 ${shadowColor}`)
        shadowLayers.push(`0px ${baseShadowY + 7}px 20px ${finalShadowColor}`)
    }

    const boxShadow = shadowLayers.join(', ')

    return {
        transform,
        boxShadow,
        width: `${props.width}%`
    }
}

// Export for potential use elsewhere
defineExpose({
    generatePhoneViewStyles
})
</script>

<style>
.device-wrapper {
    transition: all 0.8s ease;
}

.device-image {
    display: block;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.phone-view-1 {
    transform: perspective(1000px) rotateX(50deg) rotateY(0deg) rotateZ(-50deg);
    box-shadow: -3px 3px 0 #BBB, -6px 6px 0 #BBB, -9px 9px 0 #BBB, -12px 12px 0 #BBB, -14px 10px 20px #666;
}

.phone-view-2 {
    transform: perspective(1000px) rotateX(0deg) rotateY(-50deg) rotateZ(0deg);
    box-shadow: 3px 1px 0 #BBB, 9px 2px 0 #BBB, 12px 3px 0 #BBB, 10px 4px 0 #BBB, 0 7px 20px #999;
}

.phone-view-3 {
    transform: perspective(1000px) rotateX(0deg) rotateY(0deg) rotateZ(0deg);
    box-shadow: 0px 3px 0 #BBB, 0px 4px 0 #BBB, 0px 5px 0 #BBB, 0px 7px 0 #BBB, 0px 10px 20px #666;
}

.phone-view-4 {
    transform: perspective(1000px) rotateX(0deg) rotateY(30deg) rotateZ(0deg);
    box-shadow: -3px 1px 0 #BBB, -9px 2px 0 #BBB, -2px 3px 0 #BBB, -2px 4px 0 #BBB, 0 7px 20px #999;
}
</style>